import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Badge } from '@/common/ui/badge'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import { HelpCircle } from 'lucide-react'
import {
	getCategoryData,
	formatConversionRate,
	formatConversionAmount,
	formatNumber,
	CATEGORY_DESCRIPTIONS,
} from '../../utils/formatConversionData'

import type { CategoryConversionMetrics } from '../../types'
import type { ReactivationCategory } from '../../utils/formatConversionData'

interface CategoryConversionTableProps {
	metrics: CategoryConversionMetrics | undefined
	isLoading?: boolean
	/** When true, renders just the table without card wrapper (for embedding in other components) */
	embedded?: boolean
}

const CONVERSION_WINDOWS = [
	{
		key: 'day1' as const,
		label: '1 วัน',
		tooltip: 'ฝากภายใน 1 วันหลังรับข้อความ',
	},
	{
		key: 'day3' as const,
		label: '3 วัน',
		tooltip: 'ฝากภายใน 3 วันหลังรับข้อความ',
	},
	{
		key: 'day5' as const,
		label: '5 วัน',
		tooltip: 'ฝากภายใน 5 วันหลังรับข้อความ',
	},
	{
		key: 'day7' as const,
		label: '7 วัน',
		tooltip: 'ฝากภายใน 7 วันหลังรับข้อความ',
	},
	{
		key: 'day14' as const,
		label: '14 วัน',
		tooltip: 'ฝากภายใน 14 วันหลังรับข้อความ',
	},
	{
		key: 'day30' as const,
		label: '30 วัน',
		tooltip: 'ฝากภายใน 30 วันหลังรับข้อความ',
	},
]

/** Core table component without wrapper - can be used standalone or embedded */
function CategoryConversionTableCore({
	metrics,
	embedded = false,
}: {
	metrics: CategoryConversionMetrics
	embedded?: boolean
}) {
	const categories = getCategoryData(metrics)

	return (
		<div className={embedded ? 'overflow-x-auto border-y' : 'overflow-x-auto'}>
			<Table>
				<TableHeader>
					<TableRow className={embedded ? 'bg-muted/30' : ''}>
						<TableHead className="w-[180px]">
							<div className="flex items-center gap-1">
								กลุ่ม
								<Tooltip>
									<TooltipTrigger asChild>
										<HelpCircle className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
									</TooltipTrigger>
									<TooltipContent side="top" className="max-w-xs">
										<p>กลุ่มผู้รับแบ่งตามพฤติกรรมการฝาก:</p>
										<ul className="mt-1 list-disc pl-4 text-xs">
											<li>ลูกค้าใหม่: ยังไม่เคยฝากเงินมาก่อน</li>
											<li>7-13 วัน: ไม่ฝากมา 7-13 วัน</li>
											<li>14-29 วัน: ไม่ฝากมา 14-29 วัน</li>
											<li>30+ วัน: ไม่ฝากมา 30 วันขึ้นไป</li>
											<li>ลูกค้าประจำ: ฝากภายใน 7 วันที่ผ่านมา</li>
										</ul>
									</TooltipContent>
								</Tooltip>
							</div>
						</TableHead>
						<TableHead className="text-right">ผู้รับ</TableHead>
						<TableHead className="text-right">จำนวนฝาก</TableHead>
						<TableHead className="text-right">ยอดฝาก</TableHead>
						{CONVERSION_WINDOWS.map((window) => (
							<TableHead key={window.key} className="text-center">
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="cursor-help border-b border-dotted border-muted-foreground">
											{window.label}
										</span>
									</TooltipTrigger>
									<TooltipContent side="top">{window.tooltip}</TooltipContent>
								</Tooltip>
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{categories.map((category) => (
						<TableRow
							key={category.key}
							className={
								category.key === 'regularUser'
									? 'bg-muted/30'
									: embedded
										? ''
										: 'hover:bg-muted/50'
							}
						>
							<TableCell>
								<Tooltip>
									<TooltipTrigger asChild>
										<Badge className={`${category.bgColor} cursor-help`}>
											{category.label}
										</Badge>
									</TooltipTrigger>
									<TooltipContent side="right">
										{
											CATEGORY_DESCRIPTIONS[
												category.key as ReactivationCategory
											]
										}
									</TooltipContent>
								</Tooltip>
							</TableCell>
							<TableCell className="text-right text-muted-foreground">
								{formatNumber(category.recipients)}
							</TableCell>
							<TableCell className="text-right font-semibold">
								{formatNumber(category.count)}
							</TableCell>
							<TableCell className="text-right font-semibold text-green-600 dark:text-green-400">
								{formatConversionAmount(category.amount)}
							</TableCell>
							{CONVERSION_WINDOWS.map((window) => (
								<TableCell key={window.key} className="text-center">
									<div className="flex flex-col">
										<span className="font-semibold">
											{formatConversionRate(category.byDay[window.key].rate)}
										</span>
										<span className="text-xs text-muted-foreground">
											{formatNumber(category.byDay[window.key].count)} คน
										</span>
										<span className="text-xs text-muted-foreground">
											{formatConversionAmount(
												category.byDay[window.key].amount,
											)}
										</span>
									</div>
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}

export default function CategoryConversionTable({
	metrics,
	isLoading,
	embedded = false,
}: CategoryConversionTableProps) {
	const categories = getCategoryData(metrics)

	// Loading state - only show for non-embedded mode
	if (isLoading && !embedded) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Conversion ตามกลุ่มผู้รับ</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-64 animate-pulse rounded bg-muted" />
				</CardContent>
			</Card>
		)
	}

	// Empty state - only show for non-embedded mode
	if (!metrics || categories.length === 0) {
		if (embedded) return null
		return (
			<Card>
				<CardHeader>
					<CardTitle>Conversion ตามกลุ่มผู้รับ</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<p className="text-muted-foreground">ไม่มีข้อมูล</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	// Embedded mode - just return the table
	if (embedded) {
		return (
			<TooltipProvider>
				<CategoryConversionTableCore metrics={metrics} embedded />
			</TooltipProvider>
		)
	}

	// Full card mode
	return (
		<TooltipProvider>
			<Card>
				<CardHeader>
					<CardTitle>Conversion ตามกลุ่มผู้รับ (Reactivation)</CardTitle>
					<p className="text-sm text-muted-foreground">
						จำนวนผู้รับที่ฝากเงินหลังได้รับข้อความ แยกตามกลุ่มและช่วงเวลา
						(นับแยกแคมเปญกัน ดังนั้นผู้เล่นคนเดียวกันอาจถูกนับเป็นหลาย
						conversion หากได้รับข้อความจากหลายแคมเปญ)
					</p>
				</CardHeader>
				<CardContent>
					<CategoryConversionTableCore metrics={metrics} />
				</CardContent>
			</Card>
		</TooltipProvider>
	)
}
